import type {
  CustomerConfig,
  Environment,
  EntityKey,
  MappingSuggestion,
  PayloadAnalysis,
  TransformationType,
  VariableRow,
} from "./types";

export function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function tableName(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function normalizeEnvironment(value: unknown): Environment {
  if (value === "dev" || value === "acc" || value === "prod") return value;
  return "prod";
}

export function normalizeEntity(value: unknown): EntityKey {
  if (value === "docforce" || value === "customer-kpi" || value === "finance") {
    return value;
  }

  return "customer-kpi";
}

export function mapFoundryCustomer(row: any): CustomerConfig {
  const customerId = String(row.customer_id || row.customerId || "");
  const customerName = String(
    row.customer_name || row.customerName || customerId || "Unknown customer"
  );
  const environment = normalizeEnvironment(row.environment);
  const entity = normalizeEntity(row.entity);
  const entityCode = String(row.entity_code || row.entityCode || row.entity || "JDX");

  return {
    customerKey: `${customerId || slugify(customerName)}-${environment}`,
    customerName,
    customerId,
    entity,
    entityCode,
    environment,
    isActive:
      typeof row.isActive === "boolean"
        ? row.isActive
        : typeof row.is_active === "boolean"
          ? row.is_active
          : row.is_active === 1
            ? true
            : true,
    apiBaseUrl: String(row.api_base_url || row.apiBaseUrl || ""),
    targetTable: String(
      row.target_table ||
        row.targetTable ||
        `jdx_api_${tableName(customerName || customerId || "customer")}`
    ),
    dataLoaderFunction: String(
      row.data_loader_function ||
        row.dataLoaderFunction ||
        `func-jdx-${slugify(entity)}-loader-${environment}`
    ),
  };
}

export function getBlockMeta(block: string) {
  const value = block.toLowerCase();

  if (
    value.includes("http") ||
    value.includes("webhook") ||
    value.includes("email arrives") ||
    value.includes("recurrence")
  ) {
    return {
      type: "Trigger",
      accent: "border-cyan-400/70 bg-cyan-500/10 text-cyan-200",
      icon: "◎",
    };
  }

  if (
    value.includes("output") ||
    value.includes("response") ||
    value.includes("excel") ||
    value.includes("csv") ||
    value.includes("json") ||
    value.includes("blob storage")
  ) {
    return {
      type: "Output",
      accent: "border-emerald-400/70 bg-emerald-500/10 text-emerald-200",
      icon: "⇥",
    };
  }

  if (value.includes("customer") || value.includes("jdx")) {
    return {
      type: "JDX",
      accent: "border-violet-400/70 bg-violet-500/10 text-violet-200",
      icon: "◌",
    };
  }

  if (value.includes("payload") || value.includes("parse")) {
    return {
      type: "Transform",
      accent: "border-blue-400/70 bg-blue-500/10 text-blue-200",
      icon: "</>",
    };
  }

  if (value.includes("loop") || value.includes("condition") || value.includes("switch")) {
    return {
      type: "Logic",
      accent: "border-amber-400/70 bg-amber-500/10 text-amber-200",
      icon: "↻",
    };
  }

  if (value.includes("sql") || value.includes("storage") || value.includes("blob")) {
    return {
      type: "Data",
      accent: "border-blue-500/70 bg-blue-600/10 text-blue-200",
      icon: "▣",
    };
  }

  if (value.includes("teams") || value.includes("email")) {
    return {
      type: "Notify",
      accent: "border-fuchsia-400/70 bg-fuchsia-500/10 text-fuchsia-200",
      icon: "✉",
    };
  }

  return {
    type: "Custom",
    accent: "border-slate-500/70 bg-slate-500/10 text-slate-200",
    icon: "□",
  };
}

export function getTriggerTitle(triggerType: string) {
  if (triggerType === "email") return "When a new email arrives";
  if (triggerType === "recurrence") return "Recurrence";
  return "HTTP Input";
}

export function getTriggerSubtitle(triggerType: string) {
  if (triggerType === "email") return "Mailbox event trigger";
  if (triggerType === "recurrence") return "Scheduled integration trigger";
  return "Receive customer payload and generate a JDX request";
}

export function getStepId(step: { title: string }, index: number) {
  return `${index}-${step.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

export function getPathValue(source: any, path: string, fallback: any = "") {
  const value = path.split(".").reduce<any>((current, key) => {
    if (current === undefined || current === null) return undefined;
    return current[key];
  }, source);

  return value === undefined || value === null || value === "" ? fallback : value;
}

export function firstDefined(...values: any[]) {
  return values.find((value) => value !== undefined && value !== null && value !== "") ?? "";
}

export function flattenPayload(value: any, prefix = "", result: Record<string, any> = {}) {
  if (Array.isArray(value)) {
    result[prefix] = value;

    value.forEach((item, index) => {
      flattenPayload(item, `${prefix}[${index}]`, result);
    });

    if (value[0] && typeof value[0] === "object") {
      flattenPayload(value[0], `${prefix}[]`, result);
    }

    return result;
  }

  if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, childValue]) => {
      const childPath = prefix ? `${prefix}.${key}` : key;
      flattenPayload(childValue, childPath, result);
    });

    return result;
  }

  if (prefix) result[prefix] = value;
  return result;
}

export function normalizeKey(value: string) {
  return value
    .toLowerCase()
    .replace(/\[[0-9]+\]/g, "[]")
    .replace(/[^a-z0-9]+/g, "");
}

export function smartValue(flat: Record<string, any>, aliases: string[], fallback: any = "") {
  const entries = Object.entries(flat);

  for (const alias of aliases) {
    if (flat[alias] !== undefined && flat[alias] !== null && flat[alias] !== "") {
      return flat[alias];
    }
  }

  const normalizedAliases = aliases.map(normalizeKey);

  const exact = entries.find(([key, value]) => {
    if (value === undefined || value === null || value === "") return false;
    return normalizedAliases.includes(normalizeKey(key));
  });

  if (exact) return exact[1];

  const fuzzy = entries.find(([key, value]) => {
    if (value === undefined || value === null || value === "") return false;
    const normalizedKey = normalizeKey(key);
    return normalizedAliases.some(
      (alias) => normalizedKey.endsWith(alias) || normalizedKey.includes(alias)
    );
  });

  return fuzzy ? fuzzy[1] : fallback;
}

export function gramsToKg(value: any) {
  const numberValue = Number(value || 0);
  return numberValue > 0 ? Number((numberValue / 1000).toFixed(3)) : 0;
}

export function cm3ToM3(value: any) {
  const numberValue = Number(value || 0);
  return numberValue > 0 ? Number((numberValue / 1000000).toFixed(3)) : 0;
}

export function mapBuycoPackageType(value: any) {
  const packageType = String(value || "").toUpperCase();

  const map: Record<string, string> = {
    PX: "PALLET",
    PK: "PACKAGE",
    CT: "CARTON",
    BX: "BOX",
    BG: "BAG",
    DR: "DRUM",
  };

  return map[packageType] || packageType || "PACKAGE";
}

export function mapBuycoContainerIsoCode(container: any) {
  const size = String(container?.size || "");
  const type = String(container?.type || "").toUpperCase();

  if (size === "20") return "22G1";
  if (size === "40" && type.includes("HIGH")) return "45G1";
  if (size === "40") return "42G1";

  return getPathValue(container, "containerType.isoCode", "");
}

export function normalizeBookingInput(sourcePayload: any = {}) {
  const source = sourcePayload?.body || sourcePayload;
  const flat = flattenPayload(source);

  const routing = Array.isArray(source.routing) ? source.routing : [];
  const firstRouting = routing[0] || {};
  const lastRouting = routing[routing.length - 1] || {};

  const shipper =
    source.parties?.shipper?.contact ||
    source.parties?.seller?.contact ||
    source.shipperAddress ||
    source.shipper ||
    {};

  const consignee =
    source.parties?.consignee?.contact ||
    source.consigneeAddress ||
    source.consignee ||
    {};

  const notify =
    source.parties?.notify_1?.contact ||
    source.notifyPartyAddress ||
    source.notify ||
    {};

  const loadType = String(
    firstDefined(
      source.shipping_mode,
      source.loadType,
      smartValue(flat, ["loadType", "load_type", "shippingMode", "shipping_mode", "serviceType"]),
      "FCL"
    )
  ).toUpperCase();

  return {
    source,
    flat,
    loadType,
    modality: String(
      firstDefined(
        source.modality,
        smartValue(flat, ["modality", "transportMode", "transport_mode", "mode"]),
        "OCEAN"
      )
    ).toUpperCase(),
    movementType: String(
      firstDefined(
        source.movementType,
        smartValue(flat, ["movementType", "movement_type", "direction", "tradeType"]),
        "EXPORT"
      )
    ).toUpperCase(),
    customerReference: firstDefined(
      source.buyco_reference,
      source.customerReference,
      source.reference,
      source.external_id,
      smartValue(flat, [
        "customerReference",
        "customer_reference",
        "buycoReference",
        "buyco_reference",
        "reference",
        "bookingReference",
        "externalId",
        "external_id",
      ])
    ),
    cargoReadyDate: firstDefined(
      firstRouting.date,
      source.cargoReadyDate,
      smartValue(flat, ["cargoReadyDate", "cargo_ready_date", "readyDate", "pickupDate", "etd"])
    ),
    targetedArrivalDate: firstDefined(
      lastRouting.date,
      source.targetedArrivalDate,
      smartValue(flat, ["targetedArrivalDate", "targeted_arrival_date", "arrivalDate", "deliveryDate", "eta"])
    ),
    originUnLoCode: firstDefined(
      firstRouting.unlocode,
      source.originPort?.unLoCode,
      source.originPort,
      smartValue(flat, [
        "originPort.unLoCode",
        "originUnLoCode",
        "origin_unlocode",
        "pol",
        "portOfLoading",
        "port_of_loading",
        "fromUnlocode",
        "from.unlocode",
      ])
    ),
    destinationUnLoCode: firstDefined(
      lastRouting.unlocode,
      source.destinationPort?.unLoCode,
      source.destinationPort,
      source.incoterm?.unlocode,
      smartValue(flat, [
        "destinationPort.unLoCode",
        "destinationUnLoCode",
        "destination_unlocode",
        "pod",
        "portOfDischarge",
        "port_of_discharge",
        "toUnlocode",
        "to.unlocode",
      ])
    ),
    incoterm: String(
      firstDefined(
        source.incoterm?.term,
        source.incoterm,
        smartValue(flat, ["incoterm", "incotermCode", "incoterm_code", "term"])
      )
    ).toUpperCase(),
    remarks: firstDefined(
      source.instruction?.booking_instructions,
      source.instruction?.general_instructions,
      source.remarks,
      smartValue(flat, ["remarks", "notes", "instructions", "bookingInstructions", "booking_instructions"])
    ),
    shipper,
    consignee,
    notify,
    containers: Array.isArray(source.containers)
      ? source.containers
      : Array.isArray(source.containerList)
        ? source.containerList
        : [],
    packages: Array.isArray(source.packages)
      ? source.packages
      : Array.isArray(source.cargoes)
        ? source.cargoes
        : Array.isArray(source.items)
          ? source.items
          : [],
  };
}

export function mapAddress(contact: any) {
  if (!contact || !contact.address) return null;

  return {
    name: firstDefined(contact.company_name, contact.name),
    streetAddress: contact.address.street || "",
    streetAddressLine2: contact.address.street_bis || null,
    city: contact.address.city || "",
    country: contact.address.country || "",
    postalCode: contact.address.postal_code || null,
    phoneNumber: contact.phone || null,
    email: contact.email || null,
    vatNumber: contact.tax_identifier || null,
    eoriNumber: contact.eori_number || null,
  };
}

export function buildMinimalShipmentBookingPayload(sourcePayload: any = {}, selectedCustomerId = "") {
  const normalized = normalizeBookingInput(sourcePayload);
  const isPartialLoad = ["LCL", "LTL", "ULD", "LOOSE"].includes(String(normalized.loadType));

  const basePayload: Record<string, unknown> = {
    branchId: "",
    customer: {
      id:
        selectedCustomerId ||
        String(
          firstDefined(
            normalized.source?.customer?.id,
            normalized.source?.customerId,
            smartValue(normalized.flat || {}, ["customer.id", "customerId", "customer_id"]),
            "MISSING_CUSTOMER_ID"
          )
        ),
    },
    modality: normalized.modality,
    loadType: normalized.loadType,
    movementType: normalized.movementType,
    cargoReadyDate: normalized.cargoReadyDate,
    targetedArrivalDate: normalized.targetedArrivalDate,
    customerReference: normalized.customerReference,
    originPort: {
      unLoCode: normalized.originUnLoCode,
    },
    destinationPort: {
      unLoCode: normalized.destinationUnLoCode,
    },
    incoterm: normalized.incoterm,
  };

  const shipperAddress = mapAddress(normalized.shipper);
  const consigneeAddress = mapAddress(normalized.consignee);
  const notifyPartyAddress = mapAddress(normalized.notify);

  if (shipperAddress) basePayload.shipperAddress = shipperAddress;
  if (consigneeAddress) basePayload.consigneeAddress = consigneeAddress;
  if (notifyPartyAddress) basePayload.notifyPartyAddress = notifyPartyAddress;
  if (normalized.remarks) basePayload.remarks = String(normalized.remarks).slice(0, 1000);

  if (isPartialLoad) {
    basePayload.packages =
      normalized.packages.length > 0
        ? normalized.packages.map((item: any) => ({
            packageType: mapBuycoPackageType(firstDefined(item.packageType, item.package_type_code)),
            quantity: Number(firstDefined(item.quantity, item.packages_count, 1)),
            goodsDescription: firstDefined(item.goodsDescription, item.description),
            volume: Number(firstDefined(item.volume, cm3ToM3(item.volume?.value), 0)),
            weight: Number(firstDefined(item.weight, gramsToKg(item.gross_weight?.value), 0)),
            net: Number(firstDefined(item.net, gramsToKg(item.net_weight?.value), 0)),
            hsCodes: item.harmonized_system_code ? [String(item.harmonized_system_code)] : [],
          }))
        : [];
  } else {
    basePayload.containers = normalized.containers.map((container: any) => {
      const cargoes = Array.isArray(container.cargoes) ? container.cargoes : [];
      const firstCargo = cargoes[0] || {};

      return {
        containerType: {
          isoCode: mapBuycoContainerIsoCode(container),
        },
        number: firstDefined(container.identification_number, container.number),
        quantity: Number(firstDefined(container.quantity, 1)),
        goodsDescription: firstDefined(
          container.goodsDescription,
          firstCargo.description,
          cargoes.map((cargo: any) => cargo.description).filter(Boolean).join(" / ")
        ),
        gross: Number(firstDefined(container.gross, gramsToKg(firstCargo.gross_weight?.value), 0)),
        net: Number(firstDefined(container.net, gramsToKg(firstCargo.net_weight?.value), 0)),
        volume: Number(firstDefined(container.volume, cm3ToM3(firstCargo.volume?.value), 0)),
        orderNumber: firstDefined(
          firstCargo.references?.purchase_order_number,
          firstCargo.references?.sales_order_number
        ),
        hsCodes: cargoes
          .map((cargo: any) => cargo.harmonized_system_code)
          .filter(Boolean)
          .map(String),
        packages: cargoes.map((cargo: any) => ({
          packageType: mapBuycoPackageType(cargo.package_type_code),
          quantity: Number(firstDefined(cargo.packages_count, 1)),
          goodsDescription: firstDefined(cargo.description, ""),
          volume: cm3ToM3(cargo.volume?.value),
          weight: gramsToKg(cargo.gross_weight?.value),
          net: gramsToKg(cargo.net_weight?.value),
          hsCodes: cargo.harmonized_system_code ? [String(cargo.harmonized_system_code)] : [],
        })),
      };
    });
  }

  return basePayload;
}

function getArrayDescriptor(item: Record<string, unknown>) {
  const candidates = [
    "eventType",
    "event_type",
    "type",
    "status",
    "name",
    "code",
    "milestone",
    "activity",
  ];

  for (const key of candidates) {
    const value = item[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return { key, value: String(value) };
    }
  }

  return null;
}

function normalizeArrayPath(path: string) {
  return path.replace(/\[\d+\]/g, "[]");
}

export function getPayloadValueByPath(source: any, path: string): any {
  if (!path) return undefined;

  const cleanPath = path.replace(/^payload\./, "");
  const conditionalMatch = cleanPath.match(/^(.+)\[\?(.+?)=(.+?)\]\.(.+)$/);

  if (conditionalMatch) {
    const [, arrayPath, selectorKey, selectorValue, childPath] = conditionalMatch;
    const arrayValue: any[] | undefined = getPayloadValueByPath(source, arrayPath);

    if (!Array.isArray(arrayValue)) return undefined;

    const matchedItem: any | undefined = arrayValue.find(
      (item: any) => String(item?.[selectorKey]) === String(selectorValue)
    );

    if (!matchedItem) return undefined;

    return getPayloadValueByPath(matchedItem, childPath);
  }

  const parts = cleanPath.split(".");

  return parts.reduce<any>((current, rawPart) => {
    if (current === undefined || current === null) return undefined;

    const arrayMatch = rawPart.match(/^(.+)\[(\d+)\]$/);
    const wildcardMatch = rawPart.match(/^(.+)\[\]$/);

    if (arrayMatch) {
      const [, key, index] = arrayMatch;
      return current?.[key]?.[Number(index)];
    }

    if (wildcardMatch) {
      const [, key] = wildcardMatch;
      return Array.isArray(current?.[key]) ? current[key][0] : undefined;
    }

    return current?.[rawPart];
  }, source);
}

export function setPayloadValueByPath(target: Record<string, any>, path: string, value: any) {
  if (!path || value === undefined || value === null || value === "") return target;

  const parts = path.split(".");
  let current: any = target;

  parts.forEach((rawPart, index) => {
    const isLast = index === parts.length - 1;
    const arrayField = rawPart.endsWith("[]") ? rawPart.replace("[]", "") : "";

    if (arrayField) {
      if (!Array.isArray(current[arrayField])) current[arrayField] = [{}];

      if (isLast) {
        current[arrayField] = Array.isArray(value) ? value : [value];
      } else {
        if (!current[arrayField][0] || typeof current[arrayField][0] !== "object") {
          current[arrayField][0] = {};
        }
        current = current[arrayField][0];
      }

      return;
    }

    if (isLast) {
      current[rawPart] = value;
      return;
    }

    if (!current[rawPart] || typeof current[rawPart] !== "object") {
      current[rawPart] = {};
    }

    current = current[rawPart];
  });

  return target;
}

export function transformMappedValue(value: any, transformation: TransformationType) {
  if (value === undefined || value === null) return value;

  if (transformation === "uppercase") return String(value).toUpperCase();
  if (transformation === "lowercase") return String(value).toLowerCase();
  if (transformation === "trim") return String(value).trim();
  if (transformation === "number") return Number(value || 0);
  if (transformation === "boolean") {
    return value === true || String(value).toLowerCase() === "true" || String(value) === "1";
  }
  if (transformation === "grams-to-kg") return gramsToKg(value);
  if (transformation === "cm3-to-m3") return cm3ToM3(value);
  if (transformation === "buyco-package-type") return mapBuycoPackageType(value);
  if (transformation === "buyco-container-iso") return mapBuycoContainerIsoCode(value);

  return value;
}

export function inferDefaultTransformation(targetPath: string): TransformationType {
  const normalized = targetPath.toLowerCase();

  if (normalized.includes("isocode")) return "uppercase";
  if (normalized.includes("unlocode")) return "uppercase";
  if (normalized.includes("country")) return "uppercase";
  if (normalized.includes("incoterm")) return "uppercase";
  if (normalized.includes("modality")) return "uppercase";
  if (normalized.includes("loadtype")) return "uppercase";
  if (normalized.includes("movementtype")) return "uppercase";
  if (
    normalized.includes("quantity") ||
    normalized.includes("gross") ||
    normalized.includes("net") ||
    normalized.includes("volume")
  ) {
    return "number";
  }
  if (
    normalized.includes("dgenabled") ||
    normalized.includes("hasdeclaration") ||
    normalized.includes("hascarriage")
  ) {
    return "boolean";
  }

  return "none";
}

export function buildMappedPayloadFromMappings(
  sourcePayload: any,
  mappings: MappingSuggestion[],
  selectedCustomerId: string
) {
  const source = sourcePayload?.body || sourcePayload || {};
  const result: Record<string, any> = {
    branchId: "",
    customer: {
      id: selectedCustomerId || "MISSING_CUSTOMER_ID",
    },
  };

  mappings.forEach((mapping) => {
    if (mapping.target.path === "branchId" || mapping.target.path === "customer.id") return;

    const rawValue =
      mapping.constantValue !== undefined && mapping.constantValue !== ""
        ? mapping.constantValue
        : getPayloadValueByPath(source, mapping.source);

    const value = transformMappedValue(rawValue, mapping.transformation);
    setPayloadValueByPath(result, mapping.target.path, value);
  });

  return result;
}

export function analyzePayload(value: unknown, prefix = "payload"): PayloadAnalysis {
  const variables: VariableRow[] = [];
  const arrays: VariableRow[] = [];

  const pushVariable = (
    key: string,
    variableValue: string,
    source = "http-payload",
    label?: string
  ) => {
    if (variables.some((item) => item.key === key)) return;

    variables.push({
      key,
      label: label || key,
      value: variableValue,
      source,
    });
  };

  const pushArray = (key: string, length: number, label?: string) => {
    if (!arrays.some((item) => item.key === key)) {
      arrays.push({
        key,
        label: label || key,
        value: `[array:${length}]`,
        source: "http-payload",
      });
    }

    pushVariable(key, `[array:${length}]`, "http-payload", label || `${key} · ${length} regels`);
  };

  const primitiveToString = (current: unknown) => {
    if (current === null || current === undefined) return "";
    if (typeof current === "object") return JSON.stringify(current);
    return String(current);
  };

  const walkObject = (current: Record<string, unknown>, path: string) => {
    Object.entries(current).forEach(([childKey, childValue]) => {
      walk(childValue, `${path}.${childKey}`);
    });
  };

  const walkArray = (current: unknown[], path: string) => {
    const arrayLabel = `${path.replace(/^payload\./, "")} · ${current.length} regels`;
    pushArray(path, current.length, arrayLabel);

    const objectItems = current.filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object" && !Array.isArray(item)
    );

    if (objectItems.length === 0) {
      current.forEach((item, index) => {
        pushVariable(`${path}[${index}]`, primitiveToString(item), "http-payload");
      });
      return;
    }

    const fieldMap = new Map<string, { key: string; value: string; label: string }>();

    objectItems.forEach((item) => {
      const descriptor = getArrayDescriptor(item);
      const descriptorPrefix = descriptor ? `${descriptor.value} → ` : "";

      Object.entries(item).forEach(([childKey, childValue]) => {
        const wildcardPath = `${path}[].${childKey}`;
        const normalizedWildcardPath = normalizeArrayPath(wildcardPath);

        if (Array.isArray(childValue)) {
          pushArray(wildcardPath, childValue.length, `${descriptorPrefix}${childKey} · lijst`);

          childValue.forEach((nestedItem, nestedIndex) => {
            if (nestedItem && typeof nestedItem === "object" && !Array.isArray(nestedItem)) {
              Object.entries(nestedItem as Record<string, unknown>).forEach(
                ([nestedKey, nestedValue]) => {
                  const nestedPath = `${wildcardPath}[${nestedIndex}].${nestedKey}`;
                  pushVariable(
                    nestedPath,
                    primitiveToString(nestedValue),
                    "http-payload",
                    `${descriptorPrefix}${childKey} → ${nestedKey}`
                  );
                }
              );
            }
          });

          return;
        }

        if (childValue && typeof childValue === "object") {
          Object.entries(childValue as Record<string, unknown>).forEach(
            ([nestedKey, nestedValue]) => {
              const nestedWildcardPath = `${wildcardPath}.${nestedKey}`;
              const key = normalizeArrayPath(nestedWildcardPath);

              if (!fieldMap.has(key)) {
                fieldMap.set(key, {
                  key: nestedWildcardPath,
                  value: primitiveToString(nestedValue),
                  label: `${descriptorPrefix}${childKey} → ${nestedKey}`,
                });
              }
            }
          );

          return;
        }

        if (descriptor && descriptor.key === childKey) {
          return;
        }

        const key = descriptor
          ? `${path}[?${descriptor.key}=${descriptor.value}].${childKey}`
          : normalizedWildcardPath;

        if (!fieldMap.has(key)) {
          fieldMap.set(key, {
            key,
            value: primitiveToString(childValue),
            label: `${descriptorPrefix}${childKey}`,
          });
        }
      });
    });

    fieldMap.forEach((field) => {
      if (!field.key) return;
      pushVariable(field.key, field.value, "http-payload", field.label);
    });
  };

  const walk = (current: unknown, path: string) => {
    if (Array.isArray(current)) {
      walkArray(current, path);
      return;
    }

    if (current && typeof current === "object") {
      walkObject(current as Record<string, unknown>, path);
      return;
    }

    pushVariable(path, primitiveToString(current), "http-payload");
  };

  walk(value, prefix);

  return { variables, arrays };
}
